import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import axios from "axios";
import { motion } from "framer-motion";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import {
  Leaf,
  Ship,
  Truck,
  Plane,
  Train,
  Calculator,
  MapPin,
  Weight,
  DollarSign,
  Info,
  Loader2,
  ArrowRight,
  TreePine,
} from "lucide-react";


// Popular routes for quick selection
const POPULAR_ROUTES = [
  { origin: "CNSHA", destination: "SIKOP", name: "Shanghai → Koper" },
  { origin: "VNSGN", destination: "SIKOP", name: "Ho Chi Minh → Koper" },
  { origin: "HKHKG", destination: "SIKOP", name: "Hong Kong → Koper" },
  { origin: "SGSIN", destination: "SIKOP", name: "Singapore → Koper" },
  { origin: "AEJEA", destination: "SIKOP", name: "Jebel Ali → Koper" },
  { origin: "KRPUS", destination: "NLRTM", name: "Busan → Rotterdam" },
];

const TRANSPORT_MODES = [
  { id: "sea", name: "Sea Freight", icon: Ship, color: "text-blue-500", bgColor: "bg-blue-50" },
  { id: "road", name: "Road Transport", icon: Truck, color: "text-amber-500", bgColor: "bg-amber-50" },
  { id: "rail", name: "Rail Freight", icon: Train, color: "text-green-500", bgColor: "bg-green-50" },
  { id: "air", name: "Air Cargo", icon: Plane, color: "text-purple-500", bgColor: "bg-purple-50" },
];

const CONTAINER_TYPES = [
  { id: "st20", name: "20' Standard", weight: 21.8 },
  { id: "st40", name: "40' Standard", weight: 26.7 },
  { id: "hq40", name: "40' High Cube", weight: 26.5 },
  { id: "ref20", name: "20' Reefer", weight: 24.0 },
  { id: "ref40", name: "40' Reefer", weight: 30.5 },
];

export default function CO2CalculatorPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  const [formData, setFormData] = useState({
    origin: "",
    destination: "",
    transport_mode: "sea",
    container_type: "st40",
    weight_tons: "",
    is_refrigerated: false,
  });

  const handleQuickRoute = (route) => {
    setFormData(prev => ({
      ...prev,
      origin: route.origin,
      destination: route.destination,
    }));
  };

  const calculateCO2 = async () => {
    if (!formData.origin || !formData.destination) {
      toast.error("Please enter origin and destination");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        origin: formData.origin.toUpperCase(),
        destination: formData.destination.toUpperCase(),
        transport_mode: formData.transport_mode,
        container_type: formData.container_type,
        weight_tons: formData.weight_tons ? parseFloat(formData.weight_tons) : null,
        is_refrigerated: formData.is_refrigerated,
      };

      const response = await axios.post(`${API_URL}/co2/calculate`, payload);
      setResult(response.data);
      toast.success("CO2 emissions calculated!");
    } catch (error) {
      console.error("CO2 calculation error:", error);
      toast.error(error.response?.data?.detail || "Failed to calculate CO2 emissions");
    } finally {
      setLoading(false);
    }
  };

  // Calculate trees needed to offset (average tree absorbs ~22kg CO2/year)
  const treesNeeded = result ? Math.ceil(result.co2_kg / 22) : 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <Leaf className="h-8 w-8" />
            <h1 className="text-3xl font-bold">CO2 Emissions Calculator</h1>
          </div>
          <p className="text-white/80">
            Calculate the carbon footprint of your shipments and offset options
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl p-6 shadow-sm"
          >
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Calculator className="h-5 w-5 text-primary" />
              Calculate Emissions
            </h2>

            {/* Quick Routes */}
            <div className="mb-6">
              <Label className="text-sm text-muted-foreground mb-2 block">Popular Routes</Label>
              <div className="flex flex-wrap gap-2">
                {POPULAR_ROUTES.map((route) => (
                  <button
                    key={`${route.origin}-${route.destination}`}
                    onClick={() => handleQuickRoute(route)}
                    className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-primary/10 hover:border-primary transition-colors"
                  >
                    {route.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Origin & Destination */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label htmlFor="origin" className="flex items-center gap-1 mb-2">
                  <MapPin className="h-4 w-4 text-green-500" />
                  Origin Port
                </Label>
                <Input
                  id="origin"
                  placeholder="e.g., CNSHA, VNSGN"
                  value={formData.origin}
                  onChange={(e) => setFormData(prev => ({ ...prev, origin: e.target.value }))}
                  className="uppercase"
                />
              </div>
              <div>
                <Label htmlFor="destination" className="flex items-center gap-1 mb-2">
                  <MapPin className="h-4 w-4 text-red-500" />
                  Destination Port
                </Label>
                <Input
                  id="destination"
                  placeholder="e.g., SIKOP, NLRTM"
                  value={formData.destination}
                  onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                  className="uppercase"
                />
              </div>
            </div>

            {/* Transport Mode */}
            <div className="mb-6">
              <Label className="mb-2 block">Transport Mode</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {TRANSPORT_MODES.map((mode) => {
                  const Icon = mode.icon;
                  const isSelected = formData.transport_mode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      onClick={() => setFormData(prev => ({ ...prev, transport_mode: mode.id }))}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? `border-primary ${mode.bgColor}` 
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <Icon className={`h-6 w-6 mx-auto mb-1 ${isSelected ? mode.color : "text-muted-foreground"}`} />
                      <span className={`text-xs ${isSelected ? "font-medium" : "text-muted-foreground"}`}>
                        {mode.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Container Type (for sea freight) */}
            {formData.transport_mode === "sea" && (
              <div className="mb-6">
                <Label className="mb-2 block">Container Type</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {CONTAINER_TYPES.map((container) => {
                    const isSelected = formData.container_type === container.id;
                    return (
                      <button
                        key={container.id}
                        onClick={() => setFormData(prev => ({ ...prev, container_type: container.id }))}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          isSelected 
                            ? "border-primary bg-primary/5" 
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <span className={`text-sm ${isSelected ? "font-medium" : ""}`}>
                          {container.name}
                        </span>
                        <span className="text-xs text-muted-foreground block">
                          ~{container.weight}t avg
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom Weight */}
            <div className="mb-6">
              <Label htmlFor="weight" className="flex items-center gap-1 mb-2">
                <Weight className="h-4 w-4" />
                Custom Weight (tons)
                <span className="text-muted-foreground text-xs ml-1">(optional)</span>
              </Label>
              <Input
                id="weight"
                type="number"
                step="0.1"
                placeholder="Leave empty to use container average"
                value={formData.weight_tons}
                onChange={(e) => setFormData(prev => ({ ...prev, weight_tons: e.target.value }))}
              />
            </div>

            {/* Refrigerated Toggle */}
            {formData.transport_mode === "sea" && (
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_refrigerated}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_refrigerated: e.target.checked }))}
                    className="rounded border-border"
                  />
                  <span className="text-sm">Refrigerated container (higher emissions)</span>
                </label>
              </div>
            )}

            {/* Calculate Button */}
            <Button
              onClick={calculateCO2}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate CO2 Emissions
                </>
              )}
            </Button>
          </motion.div>

          {/* Results Panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            {result ? (
              <>
                {/* Main Result Card */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                      Estimated CO2 Emissions
                    </h3>
                    <Leaf className="h-6 w-6 text-green-600" />
                  </div>
                  
                  <div className="text-center py-6">
                    <div className="text-5xl font-bold text-green-700 dark:text-green-300 mb-2">
                      {result.co2_kg.toLocaleString()} kg
                    </div>
                    <div className="text-lg text-green-600 dark:text-green-400">
                      ({result.co2_tons.toFixed(3)} tons CO2)
                    </div>
                  </div>

                  {/* Route Info */}
                  <div className="flex items-center justify-center gap-3 text-sm text-green-700 dark:text-green-300 mb-4">
                    <span className="font-medium">{result.origin}</span>
                    <ArrowRight className="h-4 w-4" />
                    <span className="font-medium">{result.destination}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-white/50 dark:bg-black/20 rounded-lg p-3">
                      <span className="text-muted-foreground block">Distance</span>
                      <span className="font-semibold">{result.distance_km.toLocaleString()} km</span>
                    </div>
                    <div className="bg-white/50 dark:bg-black/20 rounded-lg p-3">
                      <span className="text-muted-foreground block">Cargo Weight</span>
                      <span className="font-semibold">{result.weight_tons} tons</span>
                    </div>
                  </div>
                </div>

                {/* Carbon Offset Card */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-amber-500" />
                    Carbon Offset Cost
                  </h3>
                  <div className="text-3xl font-bold text-amber-600 mb-2">
                    ${result.carbon_offset_usd.toFixed(2)} USD
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Based on ${result.breakdown.carbon_price_usd_per_ton}/ton market price
                  </p>
                </div>

                {/* Environmental Impact */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TreePine className="h-5 w-5 text-green-500" />
                    Environmental Impact
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">🌳</div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{treesNeeded} trees</div>
                      <p className="text-sm text-muted-foreground">
                        needed to absorb this CO2 in one year
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {[...Array(Math.min(treesNeeded, 20))].map((_, i) => (
                      <span key={i} className="text-xl">🌲</span>
                    ))}
                    {treesNeeded > 20 && <span className="text-sm text-muted-foreground self-center">+{treesNeeded - 20} more</span>}
                  </div>
                </div>

                {/* Methodology */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Info className="h-5 w-5 text-blue-500" />
                    Calculation Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Transport Mode</span>
                      <span className="font-medium capitalize">{result.transport_mode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Emission Factor</span>
                      <span className="font-medium">{result.emission_factor} g CO2/ton-km</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Methodology</span>
                      <span className="font-medium">{result.methodology}</span>
                    </div>
                    <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                      <code className="text-xs">
                        {result.breakdown.formula}
                        <br />
                        = {result.breakdown.distance_km} km × {result.breakdown.weight_tons}t × {result.breakdown.emission_factor_g_per_ton_km} g/t-km
                        <br />
                        = {result.co2_kg.toLocaleString()} kg CO2
                      </code>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-card border border-border rounded-xl p-8 text-center">
                <Leaf className="h-16 w-16 mx-auto text-green-200 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Calculate Your Carbon Footprint</h3>
                <p className="text-muted-foreground">
                  Enter your shipment details to estimate CO2 emissions and offset costs.
                </p>
                <div className="mt-6 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg text-sm text-left">
                  <p className="font-medium text-green-800 dark:text-green-200 mb-2">Supported Port Codes:</p>
                  <p className="text-green-700 dark:text-green-300">
                    <strong>Europe:</strong> SIKOP (Koper), HRFIK (Rijeka), DEHAM (Hamburg), NLRTM (Rotterdam)
                  </p>
                  <p className="text-green-700 dark:text-green-300">
                    <strong>Asia:</strong> CNSHA (Shanghai), VNSGN (Ho Chi Minh), HKHKG (Hong Kong), SGSIN (Singapore)
                  </p>
                  <p className="text-green-700 dark:text-green-300">
                    <strong>Americas:</strong> USLAX (Los Angeles), USNYC (New York)
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
